import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  ClickAwayListener,
  InputBase,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';

import type { Option } from './type';

type Props = {
  label: string;
  placeholder?: string;
  options: Option[];
  multiple?: boolean;

  value: string[] | string | null;
  onChange: (next: string[] | string | null) => void;

  showSearch?: boolean;
  showAllOption?: boolean;
  chipDisplay?: boolean;
  disabled?: boolean;
  inputBorder?: string;
  initialOptionLimit?: number;
  onOpen?: () => void;
  loading?: boolean;
  isNestedBg?:boolean;
  showFieldBackground?: boolean;
};

const normalizeToArray = (v: string[] | string | null): string[] => {
  if (!v) return [];
  return Array.isArray(v) ? v : [v];
};

const isAllValue = (v: any) => String(v ?? '').toLowerCase() === 'all';

export const getVisibleOptions = (
  options: Option[],
  search: string,
  showAllOption: boolean,
  initialOptionLimit?: number,
  selectedValues: string[] = [],
) => {
  const baseOptions = showAllOption
    ? options.filter((option) => !isAllValue(option.value))
    : options;

  const trimmedSearch = search.trim().toLowerCase();

  if (trimmedSearch) {
    const matches = baseOptions.filter((option) => option.label.toLowerCase().includes(trimmedSearch));

    if (!initialOptionLimit || initialOptionLimit <= 0) {
      return matches;
    }

    return matches.slice(0, initialOptionLimit);
  }

  if (!initialOptionLimit || initialOptionLimit <= 0 || initialOptionLimit >= baseOptions.length) {
    return baseOptions;
  }

  const initialSlice = baseOptions.slice(0, initialOptionLimit);
  const initialSliceIds = new Set(initialSlice.map((item) => item.value));
  const selectedValueSet = new Set(selectedValues);

  const MAX_EXTRA_SELECTED = initialOptionLimit;
  const selectedNotVisible: Option[] = [];
  for (const option of baseOptions) {
    if (selectedNotVisible.length >= MAX_EXTRA_SELECTED) break;
    if (initialSliceIds.has(option.value)) continue;
    if (selectedValueSet.has(option.value)) {
      selectedNotVisible.push(option);
    }
  }

  return [...initialSlice, ...selectedNotVisible];
};

type CheckboxIconProps = {
  borderColor?: string;
  checkColor?: string;
  backgroundColor?: string;
};

const UncheckedIcon = ({
  borderColor = '#AAAAAA',
  backgroundColor = '#fff',
}: CheckboxIconProps) => (
  <Box
    sx={{
      width: 18,
      height: 18,
      borderRadius: '4px',
      border: `1px solid ${borderColor}`,
      backgroundColor,
      boxSizing: 'border-box',
    }}
  />
);

const CheckedIconBox = ({
  borderColor = '#39ACE7',
  checkColor = '#39ACE7',
  backgroundColor = '#fff',
}: CheckboxIconProps) => (
  <Box
    sx={{
      width: 18,
      height: 18,
      borderRadius: '4px',
      border: `1px solid ${borderColor}`,
      backgroundColor,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxSizing: 'border-box',
    }}
  >
    <CheckIcon
      sx={{
        '&&': {
          fontSize: 14,
          color: `${checkColor} !important`,
          fill: `${checkColor} !important`,
        },
      }}
    />
  </Box>
);

export default function SearchableSelectDropdown({
  label,
  placeholder = 'Select',
  options,
  multiple = false,
  value,
  onChange,
  showSearch = true,
  showAllOption = true,
  chipDisplay = true,
  disabled,
  inputBorder,
  initialOptionLimit,
  onOpen,
  loading = false,
  isNestedBg=false,
  showFieldBackground = false,
}: Props) {
  const theme = useTheme();
  const colors = theme.palette;

  const dropdownColors = colors.filterDropdown;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 200);
    return () => clearTimeout(timer);
  }, [search]);

  const open = Boolean(anchorEl);

  const selectedArr = useMemo(() => normalizeToArray(value), [value]);

  const optionsMap = useMemo(
    () => new Map(options.map((option) => [option.value, option])),
    [options],
  );

  const selectedSet = useMemo(() => new Set(selectedArr), [selectedArr]);

  const allOption = useMemo(
    () => options.find((option) => isAllValue(option.value)),
    [options],
  );

  const optionsWithoutAll = useMemo(
    () => options.filter((option) => !isAllValue(option.value)),
    [options],
  );

  const allValuesWithoutAll = useMemo(
    () =>
      optionsWithoutAll
        .filter((option) => !option.disabled)
        .map((option) => option.value),
    [optionsWithoutAll],
  );

  const hasAllSelected = Boolean(allOption && selectedSet.has(allOption.value));

  /**
   * If actual value is ['all'], UI should behave like all child items are selected.
   */
  const effectiveSelectedArr = useMemo(() => {
    if (multiple && hasAllSelected) {
      return allValuesWithoutAll;
    }

    return selectedArr.filter((item) => !isAllValue(item));
  }, [multiple, hasAllSelected, allValuesWithoutAll, selectedArr]);

  const effectiveSelectedSet = useMemo(
    () => new Set(effectiveSelectedArr),
    [effectiveSelectedArr],
  );

  const allSelected = useMemo(() => {
    if (!multiple) return false;

    if (hasAllSelected) return true;

    return (
      allValuesWithoutAll.length > 0 &&
      allValuesWithoutAll.every((item) => selectedSet.has(item))
    );
  }, [multiple, hasAllSelected, allValuesWithoutAll, selectedSet]);

  const selectedLabels = useMemo(() => {
    const map = new Map(optionsWithoutAll.map((o) => [o.value, o.label]));

    const displaySelectedValues = multiple ? effectiveSelectedArr : selectedArr;

    return displaySelectedValues.map((v) => map.get(v) ?? v);
  }, [optionsWithoutAll, effectiveSelectedArr, selectedArr, multiple]);

  const chipsToRender = useMemo(() => {
    if (!multiple || !chipDisplay || selectedLabels.length === 0) return [];

    if (selectedLabels.length <= 3) {
      return selectedLabels.map((t, idx) => ({
        key: `${t}-${idx}`,
        label: t,
        deleteIndex: idx,
      }));
    }

    const extraCount = selectedLabels.length - 2;

    return [
      { key: `${selectedLabels[0]}-0`, label: selectedLabels[0], deleteIndex: 0 },
      { key: `${selectedLabels[1]}-1`, label: selectedLabels[1], deleteIndex: 1 },
      {
        key: `more-${label}`,
        label: `+${extraCount} ${label}`,
        deleteIndex: 2,
      },
    ];
  }, [chipDisplay, multiple, selectedLabels, label]);

  const filteredOptions = useMemo(() => {
    return getVisibleOptions(
      options,
      debouncedSearch,
      showAllOption,
      initialOptionLimit,
      effectiveSelectedArr,
    );
  }, [options, debouncedSearch, showAllOption, initialOptionLimit, effectiveSelectedArr]);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    setAnchorEl(e.currentTarget);
    // Fire only on the transition into the open state, not on every re-render.
    onOpen?.();
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearch('');
    setDebouncedSearch('');
  };

  const selectAll = () => {
    if (!multiple) return;

    if (allOption) {
      onChange([allOption.value]);
      return;
    }

    onChange(allValuesWithoutAll);
  };

  const clearAll = () => {
    onChange(multiple ? [] : null);
  };

  const toggleValue = (selectedValue: string) => {
    const selectedOption = optionsMap.get(selectedValue);

    if (selectedOption?.disabled) return;

    if (!multiple) {
      onChange(selectedValue);
      handleClose();
      return;
    }

    /**
     * Case 1:
     * User clicked actual All option from list.
     */
    if (isAllValue(selectedValue)) {
      if (allSelected) {
        clearAll();
        return;
      }

      if (allOption) {
        onChange([allOption.value]);
        return;
      }

      onChange(allValuesWithoutAll);
      return;
    }

    /**
     * Case 2:
     * If current selected value is ['all'], expand it first
     * so unchecking one child removes only that item.
     */
    const currentValues = hasAllSelected
      ? allValuesWithoutAll
      : selectedArr.filter((item) => !isAllValue(item));

    const currentValuesSet = new Set(currentValues);
    const nextValues = currentValuesSet.has(selectedValue)
      ? currentValues.filter((item) => item !== selectedValue)
      : [...currentValues, selectedValue];

    const nextValuesSet = new Set(nextValues);
    const isEveryChildSelected =
      allValuesWithoutAll.length > 0 &&
      allValuesWithoutAll.every((item) => nextValuesSet.has(item));

      /**
     * Case 3:
     * If user manually selected every child item,
     * convert to ['all'] only when actual all option exists.
     */
    if (allOption && isEveryChildSelected) {
      onChange([allOption.value]);
      return;
    }

    onChange(nextValues.length ? nextValues : []);
  };

  const handleDeleteChip = (deleteIndex: number) => {
    const valueToDelete = effectiveSelectedArr[deleteIndex];

    if (!valueToDelete) return;

    toggleValue(valueToDelete);
  };

  const displayText = useMemo(() => {
    if (multiple) {
      if (!effectiveSelectedArr.length) return placeholder;
      return ' ';
    }

    if (!selectedArr.length) return placeholder;

    return selectedLabels[0] ?? placeholder;
  }, [multiple, placeholder, effectiveSelectedArr.length, selectedArr.length, selectedLabels]);

  let fieldBackground = 'transparent';

  if (isNestedBg) {
    fieldBackground = '#E7ECF3';
  } else if (showFieldBackground) {
    fieldBackground = `${colors.DropdownV2?.background}`;
  }

  const popoverPaperSx = {
    width: anchorEl ? anchorEl.clientWidth : 320,
    borderRadius: '4px',
    overflow: 'hidden',
    border: `1px solid ${dropdownColors.panelBorder}`,
    boxShadow: '0px 10px 30px rgba(0,0,0,0.12)',
    backgroundColor: `${dropdownColors.panelBackground} !important`,
    margin: '0 !important',
  } as const;

  return (
    <Box >
      <Typography
        sx={{
          '&&': {
            fontSize: 14,
            fontWeight: 400,
            color: dropdownColors.label,
            mb: 0.75,
          },
        }}
      >
        {label}
      </Typography>

      <Paper
        onClick={handleOpen}
        elevation={0}
        sx={{
          '&&&': {
            width: isNestedBg ? '460px' : '100%',
            background: `${fieldBackground} !important`,
            backgroundColor: `${fieldBackground} !important`,
            border: inputBorder ?? dropdownColors.fieldBorder,
            borderRadius: '4px',
            px: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            height: '44px !important',
            minHeight: '44px !important',
            maxHeight: '44px !important',
            py: 0,
            '&:hover': {
              background: `${fieldBackground} !important`,
              backgroundColor: `${fieldBackground} !important`,
              border: inputBorder ?? dropdownColors.fieldBorder,
            },
          },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {multiple && chipDisplay && effectiveSelectedArr.length ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                flexWrap: 'nowrap',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              {chipsToRender.map((c) => (
                <Chip
                  key={c.key}
                  label={c.label}
                  size="small"
                  sx={{
                    '&&': {
                      m: 0,
                      margin: '0 !important',
                      bgcolor: dropdownColors.chipBackground,
                      color: dropdownColors.chipText,
                      fontWeight: 500,
                      borderRadius: '14px',
                      height: 26,
                      flex: '0 0 auto',
                      '& .MuiChip-label': {
                        px: 0.75,
                        fontSize: 12,
                        lineHeight: '26px',
                      },
                    },
                  }}
                  onDelete={(e) => {
                    e.stopPropagation();
                    handleDeleteChip(c.deleteIndex);
                  }}
                  deleteIcon={
                    <CloseIcon
                      sx={{
                        fontSize: 16,
                        color: dropdownColors.fieldClearIcon,
                      }}
                    />
                  }
                />
              ))}
            </Box>
          ) : (
            <Typography
              sx={{
                '&&': {
                  fontSize: 14,
                  fontWeight: 500,
                  color: effectiveSelectedArr.length
                    ? dropdownColors.fieldText
                    : dropdownColors.fieldPlaceholder,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  pl: 1,
                  textTransform: 'none',
                },
              }}
            >
              {displayText}
            </Typography>
          )}
        </Box>

        {effectiveSelectedArr.length > 0 && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              clearAll();
            }}
            sx={{
              '&&': {
                width: 28,
                height: 28,
                color: dropdownColors.fieldClearIcon,
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}

        {loading ? (
          <CircularProgress size={16} thickness={5} sx={{ color: dropdownColors.fieldIcon }} />
        ) : (
          <KeyboardArrowDownIcon sx={{ color: dropdownColors.fieldIcon }} />
        )}
      </Paper>

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        sx={{
          zIndex: 1300,
          width: anchorEl ? anchorEl.clientWidth : 320,
        }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            elevation={0}
            sx={{
              ...popoverPaperSx,
              backgroundColor: `${dropdownColors.panelBackground} !important`,
            }}
          >
            <Box sx={{ backgroundColor: `${dropdownColors.panelBackground} !important` }}>
              {showSearch && (
                <Box
                  sx={{
                    '&&': {
                      p: 1.25,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      backgroundColor: `${dropdownColors.searchBackground} !important`,
                      boxShadow: '0px 3px 8px rgba(0,0,0,0.15)',
                      position: 'relative',
                      zIndex: 1,
                    },
                  }}
                >
                  <SearchIcon sx={{ color: dropdownColors.searchIcon }} />

                  <InputBase
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={`Search ${label}`}
                    sx={{
                      width: '100%',
                      '&&': { fontSize: 14 },
                      '&& input': {
                        color: dropdownColors.searchText,
                        caretColor: dropdownColors.searchText,
                        fontSize: 14,
                      },
                      '&& input::placeholder': {
                        color: dropdownColors.searchPlaceholder,
                        opacity: 1,
                      },
                    }}
                  />
                </Box>
              )}

              {loading ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    py: 4,
                    backgroundColor: `${dropdownColors.panelBackground} !important`,
                  }}
                >
                  <CircularProgress size={22} thickness={4} sx={{ color: dropdownColors.searchIcon }} />
                  <Typography
                    sx={{
                      '&&': {
                        fontSize: 13,
                        color: dropdownColors.emptyText,
                      },
                    }}
                  >
                    Loading {label}...
                  </Typography>
                </Box>
              ) : (
                <List
                  dense
                  sx={{
                    py: 0,
                    maxHeight: 250,
                    overflowY: 'auto',
                    backgroundColor: `${dropdownColors.panelBackground} !important`,
                  }}
                >
                  {multiple && showAllOption && (
                    <ListItemButton
                      onClick={() => (allSelected ? clearAll() : selectAll())}
                      sx={{
                        '&&': {
                          py: 1,
                          backgroundColor: `${dropdownColors.panelBackground} !important`,
                          '&:hover': {
                            backgroundColor: `${dropdownColors.optionHoverBackground} !important`,
                          },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox
                          checked={allSelected}
                          disableRipple
                          icon={
                            <UncheckedIcon
                              borderColor="#AAAAAA"
                              backgroundColor={dropdownColors.checkboxBackground}
                            />
                          }
                          checkedIcon={
                            <CheckedIconBox
                              borderColor={dropdownColors.checkboxBorder || '#39ACE7'}
                              checkColor={dropdownColors.checkboxCheck || '#39ACE7'}
                              backgroundColor={dropdownColors.checkboxBackground}
                            />
                          }
                          sx={{ '&&': { p: 0 } }}
                        />
                      </ListItemIcon>

                      <ListItemText
                        primary={allOption?.label ?? 'ALL'}
                        primaryTypographyProps={{
                          sx: {
                            '&&': {
                              fontWeight: 400,
                              fontSize: 14,
                              color: dropdownColors.optionText,
                            },
                          },
                        }}
                      />
                    </ListItemButton>
                  )}

                  {filteredOptions.map((opt) => {
                    const checked =
                      isAllValue(opt.value)
                        ? allSelected
                        : effectiveSelectedSet.has(opt.value);

                    return (
                      <ListItemButton
                        key={opt.value}
                        disabled={opt.disabled}
                        onClick={() => !opt.disabled && toggleValue(opt.value)}
                        sx={{
                          '&&': {
                            py: 1,
                            opacity: opt.disabled ? 0.5 : 1,
                            cursor: opt.disabled ? 'not-allowed' : 'pointer',
                            backgroundColor: `${dropdownColors.panelBackground} !important`,
                            '&:hover': {
                              backgroundColor: `${dropdownColors.optionHoverBackground} !important`,
                            },
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Checkbox
                            checked={checked}
                            disabled={opt.disabled}
                            disableRipple
                            icon={
                              <UncheckedIcon
                                borderColor="#AAAAAA"
                                backgroundColor={dropdownColors.checkboxBackground}
                              />
                            }
                            checkedIcon={
                              <CheckedIconBox
                                borderColor={dropdownColors.checkboxBorder || '#39ACE7'}
                                checkColor={dropdownColors.checkboxCheck || '#39ACE7'}
                                backgroundColor={dropdownColors.checkboxBackground}
                              />
                            }
                            sx={{ '&&': { p: 0 } }}
                          />
                        </ListItemIcon>

                        <ListItemText
                          primary={opt.label}
                          primaryTypographyProps={{
                            sx: {
                              '&&': {
                                fontSize: 14,
                                fontWeight: 400,
                                color: dropdownColors.optionText,
                              },
                            },
                          }}
                        />
                      </ListItemButton>
                    );
                  })}

                  {!filteredOptions.length && (
                    <Box sx={{ p: 2, backgroundColor: `${dropdownColors.panelBackground} !important` }}>
                      <Typography
                        sx={{
                          '&&': {
                            fontSize: 13,
                            color: dropdownColors.emptyText,
                          },
                        }}
                      >
                        No results
                      </Typography>
                    </Box>
                  )}
                </List>
              )}
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
}